import React from 'react'
import clsx from 'clsx'
import { ZinIcon } from '@tnl/zin-react'
import './FormHeader.scss'
import { withBase } from '@shared/utils/basePath'

export interface FormHeaderContent {
  icon?: string | React.ReactNode
  caption?: React.ReactNode
  title?: string | React.ReactNode
  body?: string | React.ReactNode
}

export interface FormHeaderProps {
  brand?: string
  className?: string
  // Generic variants; kept open for extensibility
  variant?: 'default' | 'hero' | 'stacked'
  title?: string
  titleClassName?: string
  caption?: React.ReactNode
  captionClassName?: string
  body?: React.ReactNode
  bodyClassName?: string
  /** Optional icon to display above the caption. Accepts a Zin icon name string or a ReactNode. */
  icon?: string | React.ReactNode
  iconSet?: string
  iconClassName?: string
  iconAriaLabel?: string
  /** Content object for stacked variant - overrides individual props when variant is 'stacked' */
  content?: FormHeaderContent
  /** List of elements to show in order: 'icon', 'caption', 'title'. If empty array, renders empty div. If not provided, defaults to ['title'] */
  showElements?: ('icon' | 'caption' | 'title' | 'body')[]
  /** Optional image to display above the caption. */
  imageSrc?: string
  imageAlt?: string
  imageWidth?: string
  imageHeight?: string
  /** Semantic heading for the title line (default div). Use h1 for primary page/modal titles e.g. registration. */
  titleTag?: 'h1' | 'h2' | 'h3' | 'div'
}

/**
 * FormHeader component with support for multiple variants including a new 'stacked' layout.
 *
 * @example
 * // Default usage (backward compatible)
 * <FormHeader title="Welcome" caption="Please sign in" />
 *
 * @example
 * // Stacked variant with content object
 * <FormHeader
 *   variant="stacked"
 *   content={{
 *     icon: "user",
 *     caption: "Welcome back",
 *     title: "Sign In"
 *   }}
 * />
 *
 * @example
 * // Stacked variant with element visibility control
 * <FormHeader
 *   variant="stacked"
 *   content={{
 *     icon: "check-circle",
 *     caption: "Success!",
 *     title: "Account Created"
 *   }}
 *   showElements={['icon', 'title']}
 * />
 *
 * @example
 * // Show only icon and title
 * <FormHeader
 *   variant="stacked"
 *   content={{
 *     icon: "user",
 *     title: "Profile"
 *   }}
 *   showElements={['icon', 'title']}
 * />
 */
export const FormHeader: React.FC<FormHeaderProps> = ({
  brand = 'mk',
  className,
  variant = 'default',
  title,
  titleClassName,
  caption,
  captionClassName,
  body,
  bodyClassName,
  icon,
  iconSet,
  iconClassName,
  iconAriaLabel,
  content,
  showElements,
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  titleTag = 'div',
}) => {
  // For stacked variant, use content object if provided, otherwise fall back to individual props
  const effectiveContent =
    variant === 'stacked' && content
      ? {
          icon: content.icon ?? icon,
          caption: content.caption ?? caption,
          title: content.title ?? title,
          body: content.body ?? body,
        }
      : {
          icon,
          caption,
          title,
          body,
        }

  const iconNode = React.useMemo<React.ReactNode | null>(() => {
    const iconToUse = effectiveContent.icon
    if (!iconToUse) return null
    if (typeof iconToUse === 'string') {
      // Determine size based on iconClassName - if it includes 'large', use 'large' size
      const isLargeIcon = iconClassName?.includes('large')
      return (
        <ZinIcon
          set={iconSet}
          className="h-[60px] w-auto"
          name={iconToUse}
          size={isLargeIcon ? 'large' : undefined}
          aria-label={iconAriaLabel ?? iconToUse}
        />
      )
    }
    return iconToUse
  }, [effectiveContent.icon, iconAriaLabel, iconClassName, iconSet])

  // Determine what to render based on variant and showElements
  const shouldShowIcon = variant === 'stacked' ? (showElements?.includes('icon') ?? false) && !!effectiveContent.icon : !!effectiveContent.icon

  const shouldShowCaption = variant === 'stacked' ? (showElements?.includes('caption') ?? false) && !!effectiveContent.caption : !!effectiveContent.caption

  const shouldShowTitle = variant === 'stacked' ? (showElements?.includes('title') ?? true) && !!effectiveContent.title : !!effectiveContent.title

  const shouldShowBody = variant === 'stacked' ? (showElements?.includes('body') ?? false) && !!effectiveContent.body : !!effectiveContent.body

  // If showElements is explicitly an empty array, render nothing
  if (variant === 'stacked' && showElements && showElements.length === 0) {
    return (
      <div
        className={clsx('form-header', variant && `form-header--${variant}`, className)}
        data-testid={`${brand}-form-header`}
      />
    )
  }

  return (
    <div
      className={clsx('form-header', variant && `form-header--${variant}`, className)}
      data-testid={`${brand}-form-header`}>
      {shouldShowIcon && (
        <div
          data-testid={`${brand}-form-icon`}
          className={clsx('form-header__icon', iconClassName)}>
          {iconNode}
        </div>
      )}
      {imageSrc && (
        <img
          src={withBase(imageSrc)}
          alt={imageAlt}
          style={{ width: imageWidth, height: imageHeight }}
          className="form-header__image self-center"
        />
      )}
      <div className="flex flex-col justify-center gap-3">
        {shouldShowCaption && (
          <div
            data-testid={`${brand}-form-caption`}
            className={clsx('form-header__caption caption-1', captionClassName)}>
            {effectiveContent.caption}
          </div>
        )}
        {shouldShowTitle &&
          React.createElement(
            titleTag,
            {
              'data-testid': `${brand}-form-title`,
              className: clsx('form-header__title', titleClassName),
            },
            effectiveContent.title,
          )}
        {shouldShowBody && (
          <div
            data-testid={`${brand}-form-body`}
            className={clsx('form-header__body', bodyClassName)}>
            {effectiveContent.body}
          </div>
        )}
      </div>
    </div>
  )
}
